import { create } from "zustand";
import { type ReactNode } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "motion/react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";

type ToastTone = "ok" | "warn" | "info" | "error";
interface Toast { id: string; tone: ToastTone; title: string; desc?: string }

interface ToastState {
  toasts: Toast[];
  push: (t: Omit<Toast, "id">) => void;
  dismiss: (id: string) => void;
}

export const useToast = create<ToastState>((set) => ({
  toasts: [],
  push: (t) => {
    const id = Math.random().toString(36).slice(2);
    set((s) => ({ toasts: [...s.toasts, { ...t, id }] }));
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })), 4200);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
}));

const toneIcon: Record<ToastTone, ReactNode> = {
  ok: <CheckCircle2 size={18} className="text-[var(--color-support)]" />,
  warn: <AlertCircle size={18} className="text-[var(--color-secondary)]" />,
  info: <Info size={18} className="text-[var(--color-primary)]" />,
  error: <AlertCircle size={18} className="text-[var(--color-danger)]" />,
};

export function ToastHost() {
  const { toasts, dismiss } = useToast();
  return createPortal(
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 max-w-sm pointer-events-none">
      <AnimatePresence>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            layout
            initial={{ opacity: 0, x: 40, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 40, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            className="pointer-events-auto flex items-start gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-pop px-4 py-3"
          >
            <div className="mt-0.5">{toneIcon[t.tone]}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[var(--color-ink)]">{t.title}</div>
              {t.desc && <div className="text-xs text-[var(--color-ink-muted)] mt-0.5">{t.desc}</div>}
            </div>
            <button
              onClick={() => dismiss(t.id)}
              className="text-[var(--color-ink-faint)] hover:text-[var(--color-ink)]"
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>,
    document.body,
  );
}
